import { mean, deviation } from "d3-array";
import { isDefined, slidingWindow, getSourcePathAccessor } from "../utils";
import { RelativeVolatility as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { stdPeriod, smoothingPeriod, sourcePath } = options;
    const source = getSourcePathAccessor(sourcePath);

    // Step 1: Calculate standard deviation over stdPeriod
    const stdAlgorithm = slidingWindow()
      .windowSize(stdPeriod)
      .accumulator((values) => {
        return deviation(values, (d) => source(d));
      });

    const stdData = stdAlgorithm(data);

    // Step 2: Separate gains and losses based on price direction
    let prevAvgGain, prevAvgLoss;
    const relVolAlgorithm = slidingWindow()
      .windowSize(smoothingPeriod)
      .accumulator((values) => {
        const lastIndex = values.length - 1;
        const currentStd = stdData[data.indexOf(values[lastIndex])];

        if (!isDefined(currentStd)) {
          return undefined;
        }

        // Check if price increased or decreased
        const currentPrice = source(values[lastIndex]);
        const prevPrice =
          lastIndex > 0
            ? source(values[lastIndex - 1])
            : source(data[data.indexOf(values[lastIndex]) - 1]);

        let gain = 0;
        let loss = 0;

        if (isDefined(prevPrice) && currentPrice > prevPrice) {
          gain = currentStd;
        } else if (isDefined(prevPrice)) {
          loss = currentStd;
        }

        // Calculate EMA-style smoothed averages
        const avgGain = isDefined(prevAvgGain)
          ? (prevAvgGain * (smoothingPeriod - 1) + gain) / smoothingPeriod
          : mean(
              values.slice(0, smoothingPeriod).map((_, i) => {
                const idx = data.indexOf(values[0]) + i;
                const std = stdData[idx];
                const price = source(data[idx]);
                const pPrice = idx > 0 ? source(data[idx - 1]) : price;
                return isDefined(pPrice) && price > pPrice ? std || 0 : 0;
              }),
            );

        const avgLoss = isDefined(prevAvgLoss)
          ? (prevAvgLoss * (smoothingPeriod - 1) + loss) / smoothingPeriod
          : mean(
              values.slice(0, smoothingPeriod).map((_, i) => {
                const idx = data.indexOf(values[0]) + i;
                const std = stdData[idx];
                const price = source(data[idx]);
                const pPrice = idx > 0 ? source(data[idx - 1]) : price;
                return isDefined(pPrice) && price <= pPrice ? std || 0 : 0;
              }),
            );

        prevAvgGain = avgGain;
        prevAvgLoss = avgLoss;

        // Calculate Relative Volatility Index
        const sum = avgGain + avgLoss;
        if (sum === 0) return 50; // Neutral when no volatility

        return (100 * avgGain) / sum;
      });

    const relVolData = relVolAlgorithm(data);

    return relVolData;
  }

  calculator.undefinedLength = function () {
    const { stdPeriod, smoothingPeriod } = options;
    return stdPeriod + smoothingPeriod - 2;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
